import React, { useEffect, useState, useCallback } from 'react';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getIntegrationNodes,
  deleteIntegrationNode,
  bulkDeleteIntegrationNodes,
  clearIntegrationNodes,
} from '../../../../redux/slices/lists/integrationNodesSlice';
import Button from 'components/base/Button';
import { Modal, Dropdown, Form, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';

const HTTP_METHOD_COLORS: Record<string, string> = {
  GET: 'success',
  POST: 'primary',
  PUT: 'warning',
  PATCH: 'info',
  DELETE: 'danger',
};

const ListIntegrationNodes = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { integrationId } = useParams<{ integrationId: string }>();

  const { nodes, parentIntegration, status, error, loading } = useAppSelector((state) => state.listOfIntegrationNodes);

  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<number>>(new Set());

  const isLoading = loading.getIntegrationNodes;

  // Combined effect: clear then fetch when integrationId changes
  useEffect(() => {
    if (integrationId) {
      dispatch(clearIntegrationNodes() as any);
      dispatch(getIntegrationNodes({ integrationId: Number(integrationId) }) as any);
    }
  }, [dispatch, integrationId]);

  const toggleSelectNode = (nodeId: number) => {
    setSelectedNodeIds((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedNodeIds.size === nodes.length) {
      setSelectedNodeIds(new Set());
    } else {
      setSelectedNodeIds(new Set(nodes.map((n: any) => n.node_id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedNodeIds.size > 0 && integrationId) {
      const result = await dispatch(
        bulkDeleteIntegrationNodes({
          integrationId: Number(integrationId),
          nodeIds: Array.from(selectedNodeIds),
        }) as any
      );
      if (!result.error) {
        setSelectedNodeIds(new Set());
      }
    }
    setBulkDeleteConfirm(false);
  };

  const confirmDeleteNode = () => {
    if (deleteConfirmId !== null && integrationId) {
      dispatch(
        deleteIntegrationNode({
          integrationId: Number(integrationId),
          nodeId: deleteConfirmId,
        }) as any
      );
    }
    setDeleteConfirmId(null);
  };

  const integrationName = parentIntegration?.name || 'Integration';

  return (
    <div>
      {/* Header */}
      <div className="mb-4">
        <h3>Integration Nodes</h3>
        <p className="text-muted mb-0">
          Configuration &gt;{' '}
          <span className="text-primary" style={{ cursor: 'pointer' }} onClick={() => navigate('/configuration/integrations')}>
            Integration Wizard
          </span>{' '}
          &gt; {integrationName} &gt; Integration Nodes
        </p>
      </div>

      {/* Toolbar */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          {selectedNodeIds.size > 0 && (
            <Button variant="danger" size="sm" onClick={() => setBulkDeleteConfirm(true)}>
              Delete ({selectedNodeIds.size})
            </Button>
          )}
        </div>
        <Button variant="primary" onClick={() => navigate(`/configuration/integrations/${integrationId}/nodes/new`)} disabled={isLoading}>
          + New Node
        </Button>
      </div>

      {/* Error */}
      {error && <div className="alert alert-danger mb-3">{error}</div>}

      {/* Table */}
      <div className="table-responsive" style={{ overflow: 'visible' }}>
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th style={{ width: '40px' }}>
                <Form.Check type="checkbox" checked={nodes.length > 0 && selectedNodeIds.size === nodes.length} onChange={toggleSelectAll} />
              </th>
              <th>HTTP Method</th>
              <th>Name</th>
              <th>Description</th>
              <th>Zone</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center py-4">
                  Loading...
                </td>
              </tr>
            ) : nodes.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-4 text-muted">
                  No nodes found. Create a new node to get started.
                </td>
              </tr>
            ) : (
              nodes.map((node: any) => (
                <tr key={node.node_id}>
                  <td>
                    <Form.Check type="checkbox" checked={selectedNodeIds.has(node.node_id)} onChange={() => toggleSelectNode(node.node_id)} />
                  </td>
                  <td>
                    <Badge bg={HTTP_METHOD_COLORS[(node.http_method || 'GET').toUpperCase()] || 'secondary'}>{(node.http_method || 'GET').toUpperCase()}</Badge>
                  </td>
                  <td>{node.name}</td>
                  <td>{node.description || ''}</td>
                  <td>{node.zone_name || ''}</td>
                  <td>
                    <Dropdown>
                      <Dropdown.Toggle variant="phoenix-secondary" className="no-caret" size="sm">
                        <FontAwesomeIcon icon={faEllipsis} className="fs-10" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => navigate(`/configuration/integrations/${integrationId}/nodes/${node.node_id}/edit`)}>
                          <FontAwesomeIcon icon={faPen} className="me-2" />
                          Edit
                        </Dropdown.Item>
                        <Dropdown.Item className="text-danger" onClick={() => setDeleteConfirmId(node.node_id)}>
                          <FontAwesomeIcon icon={faTrash} className="me-2" />
                          Delete
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Single Delete Confirmation Modal */}
      <Modal show={deleteConfirmId !== null} onHide={() => setDeleteConfirmId(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete this node?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeleteConfirmId(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDeleteNode}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Delete Confirmation Modal */}
      <Modal show={bulkDeleteConfirm} onHide={() => setBulkDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Bulk Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>Are you sure you want to delete {selectedNodeIds.size} node(s)?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBulkDeleteConfirm(false)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleBulkDelete}>
            Delete {selectedNodeIds.size} Node(s)
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ListIntegrationNodes;
